import { FC, ReactNode, useEffect, useState } from "react";
import { TableColumn } from "react-data-table-component";
import moment from "moment";
import { DateRangePicker } from "rsuite";
import { mutate } from "swr";
import 'moment-duration-format';
import startOfMonth from 'date-fns/startOfMonth';
import { useIntl } from "react-intl";
import usePredefinedRanges from "../../hooks/PredefinedRanges";
import useCalendarLocale from "../../hooks/CalendarLocale";
import { customNumberFormat, currencyFormatter, getCurrencySymbol } from "../../functions/general";
import useDynamicTab from "../../hooks/useDynamicTab";
import { MasterDataTable } from "../DataTable/MasterDataTable";
import { useSettingsStore } from "../../stores/SettingsStore";
import { ProductsSold } from "../../interfaces/ProductsSold";
import Select from 'react-select'
import { endOfMonth } from "date-fns";
import { useDataTableStore } from "../../stores/DataTableStore";
import { FilterReportProductsSold } from "./FilterReportProductsSold";
import { DataTableExport } from "../DataTable/DataTableExport";

const ReportProductsSold: FC = () => {
    const API_URL = process.env.REACT_APP_API_URL
    const [startDate, setStartDate] = useState<Date>(startOfMonth(new Date()));
    const [endDate, setEndDate] = useState<Date>(endOfMonth(new Date()));
    const [typeDate, setTypeDate] = useState('CREATED');
    const [loading, setLoading] = useState<boolean>(true);
    const [isMobile, setIsMobile] = useState<boolean>(false);
    const intl = useIntl();
    const { settings } = useSettingsStore()
    const { masterDateRange } = useDataTableStore()

    const predefinedRanges = usePredefinedRanges();
    const calendarLocale = useCalendarLocale();

    const optionsTypeDate = [
        { value: 'CREATED', label: intl.formatMessage({ id: "MASTERS.DATATABLE.TYPE.DATE_CREATED" }) },
        { value: 'STARTED', label: intl.formatMessage({ id: "MASTERS.DATATABLE.TYPE.DATE_STARTED" }) },
        { value: 'ENDED', label: intl.formatMessage({ id: "MASTERS.DATATABLE.TYPE.DATE_ENDED" }) },
    ]

    const tableColumns: TableColumn<ProductsSold>[] = [
        {
            name: `Database ${settings.pipeline_title}`,
            selector: row => row.templateName,
            sortable: true,
            width: '200px'
        },
        {
            name: 'Sales',
            selector: row => row.PICName,
            sortable: true,
            width: '200px',
        },
        {
            name: settings.merchant_title,
            selector: row => row.merchantName,
            sortable: true,
            width: '200px',
        },
        {
            name: intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.CITY' }),
            selector: row => row.merchantCity || '',
            sortable: true,
            width: '150px',
        },
        {
            name: intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.INVOICE_DATE' }),
            selector: row => row.invoiceDate || '',
            sortable: true,
            cell: row => row.invoiceDate ? moment(row.invoiceDate).format("DD MMM YYYY") : "-",
            width: '150px',
        },
        {
            name: intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.SKU' }),
            selector: row => row.sku || '',
            sortable: true,
            width: '150px',
            cell: row => row.sku || '-',
        },
        {
            name: intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.PRODUCT_NAME' }),
            selector: row => row.name,
            sortable: true,
            width: '200px',
        },
        {
            name: intl.formatMessage({ id: "FORM.LABEL.QUANTITY" }),
            selector: row => Number(row.qty),
            sortable: true,
            width: '100px',
            cell: row => customNumberFormat(row.qty),
            style: {
                justifyContent: 'end'
            }
        },
        {
            name: intl.formatMessage({ id: "MASTERS.DATATABLE.COLUMNS.SALE_PRICE" }),
            selector: row => Number(row.basePrice),
            sortable: true,
            width: '150px',
            style: {
                justifyContent: 'space-between'
            },
            cell: row => {
                return (
                    <>
                        <span>{getCurrencySymbol(row.currencyCode)}</span><span>{row.basePrice && currencyFormatter(row.basePrice)}</span>
                    </>
                )
            },
        },
        {
            name: intl.formatMessage({ id: "MASTERS.DATATABLE.COLUMNS.TOTAL_SALE" }),
            selector: row => Number(row.totalPrice),
            sortable: true,
            width: '150px',
            style: {
                justifyContent: 'space-between'
            },
            cell: row => {
                return (
                    <>
                        <span>{getCurrencySymbol(row.currencyCode)}</span><span>{row.totalPrice && currencyFormatter(row.totalPrice)}</span>
                    </>
                )
            },
        },
    ];

    let actions: ReactNode[] = [
        <DataTableExport source="reportProductsSold" columns={[]} />,
        <FilterReportProductsSold />
    ]

    useEffect(() => {
        if ((startDate && endDate && typeDate) || (masterDateRange.reportProductsSold?.startDate && masterDateRange.reportProductsSold?.endDate && typeDate)) {
            mutate(`${API_URL}products/report`);
        }
    }, [startDate, endDate, typeDate, masterDateRange.reportProductsSold?.startDate, masterDateRange.reportProductsSold?.endDate]);

    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth < 666) {
                setIsMobile(true);
            } else {
                setIsMobile(false);
            }
        };

        // Initial check
        handleResize();

        window.addEventListener("resize", handleResize);

        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, []);

    useDynamicTab({ title: `${intl.formatMessage({ id: 'MENU.REPORT_WITH_TITLE' }, { title: intl.formatMessage({ id: 'FORM.LABEL.PRODUCT_SOLD' }) })} | CRM WooBlazz` });

    return (
        <>
            <div className="row justify-content-between mb-3">
                <div className="col-lg-4 col-sm-3 col-xl-5" style={{ alignSelf: "center" }}>
                    <ol className="breadcrumb text-muted fs-6 fw-bold">
                        <li className="breadcrumb-item text-dark">{intl.formatMessage({ id: 'MENU.REPORT_WITH_TITLE' }, { title: intl.formatMessage({ id: 'FORM.LABEL.PRODUCT_SOLD' }) })}</li>
                    </ol>
                </div>
                <div className="col-lg-8 col-sm-9 col-xl-7">
                    <div className="d-flex float-end px-3 gap-5">
                        <Select
                            styles={{
                                control: (baseStyles, state) => ({
                                    ...baseStyles,
                                    border: "none", // Menghilangkan simbol garis
                                    backgroundColor: "none",
                                    maxWidth: "200px"
                                }),
                                option: (baseStyles, { isSelected, isFocused }) => ({
                                    ...baseStyles,
                                    backgroundColor: isSelected ? '#2684FF' : isFocused ? '#dcf2ff' : 'none',
                                    cursor: 'pointer'
                                }),
                            }}
                            theme={(theme) => ({
                                ...theme,
                                colors: {
                                    ...theme.colors,
                                    primary: 'none',
                                },
                            })}
                            onChange={(data: any) => { setTypeDate(data.value) }}
                            value={optionsTypeDate.find(option => option.value === typeDate)}
                            className='react-select-styled z-index-3'
                            classNamePrefix='react-select'
                            options={optionsTypeDate}
                            placeholder='Select an option'
                            isSearchable={false}
                        />

                        <DateRangePicker
                            cleanable={false}
                            character=" - "
                            format="dd MMM yyyy"
                            locale={calendarLocale}
                            value={[startDate, endDate]}
                            ranges={predefinedRanges}
                            placement="bottomEnd"
                            onChange={(value) => {
                                if (value && value.length === 2) {
                                    setStartDate(value[0]);
                                    setEndDate(value[1]);
                                }
                            }}
                            showOneCalendar={isMobile}
                            disabled={loading}
                            isoWeek={true}
                        />
                    </div>
                </div>
            </div>
            <MasterDataTable
                tableKey="reportProductsSold"
                tableColumns={tableColumns}
                apiURL={`products/report`}
                setLoading={setLoading}
                actions={actions}
                order="desc"
                customParams={typeDate}
                dateStartFilter={masterDateRange.reportProductsSold?.startDate ? moment(masterDateRange.reportProductsSold?.startDate).format('YYYY-MM-DD') : undefined}
                dateEndFilter={masterDateRange.reportProductsSold?.endDate ? moment(masterDateRange.reportProductsSold?.endDate).format('YYYY-MM-DD') : undefined}
                dateStart={startDate ? moment(startDate).format('YYYY-MM-DD') : ''}
                dateEnd={endDate ? moment(endDate).format('YYYY-MM-DD') : ''}
            />
        </>
    )
}

export { ReportProductsSold }